import { ListItemIcon } from "@material-ui/core";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListItemText from "@material-ui/core/ListItemText";
import { styled } from "@material-ui/core/styles";
import DoneIcon from "@material-ui/icons/Done";
import { observer } from "mobx-react";
import * as React from "react";
import { useLocation } from "react-router";
import { THEME } from "../../theme/ThemeStore";
import { MessageIDS, t } from "../../i18n/util";
import { applicationStore } from "../../stores/ApplicationStore";
import { pushRoute } from "../app/router/history";

export const StyledDoneIcon = styled(DoneIcon)({
    width: 18
});

type SideBarElement = {
    name: MessageIDS;
    isEnabled: boolean;
    url: string;
    alreadyFilled: boolean;
};

type SideBarProps = {
    elements: SideBarElement[];
};

type currentColors = {
    current: string;
    done: string;
    missing: string;
};

export const SideBar = observer(({ elements }: SideBarProps) => {
    const { pathname } = useLocation();
    const [activeIndex, setActiveIndex] = React.useState(0);

    React.useEffect(() => {
        const index = elements.findIndex((element) => pathname === element.url);
        setActiveIndex(index);
    }, [elements, pathname]);

    const handleClickListItem = (index: number) => {
        if (activeIndex > index) {
            if (!applicationStore.canNavigate) {
                applicationStore.setCurrentAndNextStepsToUnfinished(activeIndex);
            }
            pushRoute(elements[index].url);
        } else if (applicationStore.canNavigate) {
            pushRoute(elements[index].url);
        } else if (applicationStore.validateForm) {
            applicationStore.validateForm();
        }
    };

    const currentColors: currentColors = {
        current: THEME.customColors.sideBarCurrent,
        done: THEME.customColors.sideBarDone,
        missing: THEME.customColors.sideBarMissing
    };

    return (
        <List
            style={{
                padding: 64,
                borderRight: "none"
            }}
            className="sideBar"
        >
            {elements.map((element, index) => {
                const render = () => {
                    if (index === activeIndex) {
                        return "current";
                    } else if (element.alreadyFilled) {
                        return "done";
                    }
                    return "missing";
                };

                return (
                    <div key={element.name.toString()}>
                        <ListItem
                            button
                            disabled={!element.isEnabled}
                            onClick={() => {
                                handleClickListItem(index);
                            }}
                        >
                            <ListItemIcon>
                                <Circle color={render()} value={index + 1} currentColors={currentColors} />
                            </ListItemIcon>
                            <ListItemText>
                                <Text color={render()} value={t(element.name)} currentColors={currentColors} />
                            </ListItemText>
                        </ListItem>
                        {index !== elements.length - 1 && (
                            <React.Fragment>
                                <VerticalLine color={render()} currentColors={currentColors} />
                            </React.Fragment>
                        )}
                    </div>
                );
            })}
        </List>
    );
});

type SideBarTextProps = {
    color: keyof currentColors;
    value: string;
    currentColors: currentColors;
};

const Text = ({ color, value, currentColors }: SideBarTextProps) => {
    return (
        <div
            style={{
                color: currentColors[color]
            }}
        >
            {value}
        </div>
    );
};

type SideBarLineProps = {
    color: keyof currentColors;
    currentColors: currentColors;
};

const VerticalLine = ({ color, currentColors }: SideBarLineProps) => {
    return (
        <div
            style={{
                backgroundColor: currentColors[color],
                width: 2,
                height: 40,
                marginLeft: 28
            }}
        />
    );
};

type SideBarCircleProps = {
    color: keyof currentColors;
    value?: number;
    currentColors: currentColors;
};

const Circle = ({ color, value, currentColors }: SideBarCircleProps) => {
    return (
        <div
            style={{
                height: 26,
                width: 26,
                borderColor: currentColors[color],
                borderRadius: "50%",
                borderStyle: "solid",
                borderWidth: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: color === "done" ? THEME.customColors.darkPrimary : currentColors[color],
                backgroundColor: color === "done" ? currentColors[color] : "transparent"
            }}
        >
            {color === "done" ? <StyledDoneIcon /> : value}
        </div>
    );
};
